import {
  CommercialOffer,
  useCommercialOfferByIdQuery,
  useGenerateCommercialOfferMutation,
  useShippingAddressQuery,
  useUpdateCommercialOfferMutation,
} from 'api';
import { Form } from 'components';
import { ShippingInfo } from 'typings/client';
import { ADMIN_ROUTES } from 'constants/routes';
import { getUpdatedPayload } from 'utils/helpers';
import { useNavigate, useParams } from 'react-router-dom';
import { useCountryMapping } from 'hooks/useCountryMapping';
import { CommercialOfferActions } from 'models/CommercialOffer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { getAvailableSendActions } from 'feature/dealTabs/commercialOffers/utils';

interface MenuItemType {
  label: React.ReactNode;
  key: string;
  icon?: React.ReactNode;
  children?: MenuItemType[];
  // other props as needed
}

export const useEdit = () => {
  const [addModalState, setAddModalState] = useState<boolean>(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { handleError, handleSuccess } = useNotificationMessages();
  const { countryMapping } = useCountryMapping();
  const { commercialOfferId, id } = useParams() as { commercialOfferId: string; id: string };
  const [updateCommercialOffer] = useUpdateCommercialOfferMutation();
  const [generateCommercialOffer] = useGenerateCommercialOfferMutation();
  const {
    data: commercialOfferDetails = {} as CommercialOffer,
    isLoading,
    isFetching,
  } = useCommercialOfferByIdQuery({
    id: commercialOfferId,
  });
  const { data: { data: clientShippingAddress = [] } = {} } = useShippingAddressQuery(
    { id: commercialOfferDetails.clientId },
    {
      skip: !commercialOfferDetails.clientId,
    }
  );

  const formattedShippingData = useMemo(
    () =>
      clientShippingAddress.map((_data: ShippingInfo): { label: string; value: string } => {
        const country = countryMapping[_data.countryId];
        const fullAddress = [country?.commonName, _data.streetAddress, _data.city, _data.state, _data.zip]
          .filter(Boolean)
          .join(', ');
        return {
          label: fullAddress,
          value: _data.id,
        };
      }),
    [clientShippingAddress, countryMapping]
  );

  const availableActions = useMemo(() => {
    const actions = getAvailableSendActions(
      commercialOfferDetails.status,
      commercialOfferDetails.paymentTerms?.paymentTerms as string
    );

    return actions
      .map((actionKey: string) => {
        const action = Object.values(CommercialOfferActions).find((a) => a.value === actionKey);
        if (!action) return null;

        return {
          key: action.value,
          label: action.label,
        };
      })
      .filter(Boolean) as MenuItemType[];
  }, [commercialOfferDetails.paymentTerms?.paymentTerms, commercialOfferDetails.status]);

  const navigateToCards = useCallback(() => {
    navigate(`${ADMIN_ROUTES.deals.view}/${id}/commercial-offers`);
  }, [navigate, id]);

  const onAddModalStateChange = useCallback(() => {
    setAddModalState((prevState) => !prevState);
  }, []);

  const onFinish = useCallback(async () => {
    try {
      const result = await form.validateFields();
      const payload = getUpdatedPayload(result, commercialOfferDetails, ['customShippingAddressId']);
      payload.brands = undefined;
      const { error }: any = await updateCommercialOffer({
        id: commercialOfferId,
        ...payload,
      });
      if (error) {
        handleError(error);
      }
    } catch (err) {
      console.warn(err);
    }
  }, [updateCommercialOffer, form, handleError, commercialOfferId, commercialOfferDetails]);

  const handleFormItemBlur = useCallback(
    async (fieldKey: string, value: string | number, purchaseRequestId: string) => {
      try {
        await form.validateFields();
        const { error }: any = await updateCommercialOffer({
          id: commercialOfferId,
          brands: [
            {
              purchaseRequestId,
              [fieldKey]: value,
            },
          ],
        });
        if (error) {
          handleError(error);
        }
      } catch (err) {
        console.warn(err);
      }
    },
    [commercialOfferId, updateCommercialOffer, handleError, form]
  );
  const handleMenuClick = useCallback(
    async (info: any) => {
      const { key } = info;
      const sendOfferUrl = ADMIN_ROUTES.deals.sendCommercialOffers
        .replace(':id', id as string)
        .replace(':offerId', commercialOfferId);
      switch (key) {
        case 'SEND_COMMERCIAL_OFFER':
          navigate(sendOfferUrl + `?action=${CommercialOfferActions.SEND_COMMERCIAL_OFFER.value}`);
          return;
        case 'SEND_PRE_INVOICE':
          navigate(sendOfferUrl + `?action=${CommercialOfferActions.SEND_PRE_INVOICE.value}`);
          return;
        case 'SEND_ORDER_CONFIRMATION':
          navigate(sendOfferUrl + `?action=${CommercialOfferActions.SEND_ORDER_CONFIRMATION.value}`);
          return;
        case 'generateOffer':
          try {
            const { error } = await generateCommercialOffer({ commercialOfferId });
            if (error) {
              handleError(error);
            } else {
              handleSuccess(`Commercial offer successfully generated.`);
            }
          } catch (err) {
            handleError(err);
          }
          return;
        case 'generatePreInvoice':
          try {
            const { error } = await generateCommercialOffer({ commercialOfferId });
            if (error) {
              handleError(error);
            } else {
              handleSuccess(`Commercial offer pre invoice successfully generated.`);
            }
          } catch (err) {
            handleError(err);
          }
          return;
        case 'generateOrderConfirmation':
          try {
            const { error } = await generateCommercialOffer({ commercialOfferId });
            if (error) {
              handleError(error);
            } else {
              handleSuccess(`Commercial order confirmation successfully generated.`);
            }
          } catch (err) {
            handleError(err);
          }
          return;
      }
    },
    [commercialOfferId, id, generateCommercialOffer, navigate, handleError, handleSuccess]
  );

  useEffect(() => {
    if (commercialOfferDetails) {
      form.setFieldsValue(commercialOfferDetails);
    }
  }, [form, commercialOfferDetails]);
  return {
    form,
    onFinish,
    addModalState,
    navigateToCards,
    handleMenuClick,
    commercialOfferId,
    handleFormItemBlur,
    onAddModalStateChange,
    deliveryDetails: commercialOfferDetails.delivery || {},
    paymentTerms: commercialOfferDetails.paymentTerms || {},
    brands: commercialOfferDetails.brands || [],
    commercialOfferDetails,
    formattedShippingData,
    isFinalStatus: commercialOfferDetails.isFinalStatus,
    availableActions,
    isLoading: isLoading || isFetching,
  };
};
